import React from "react";
import { Accept, useDropzone } from "react-dropzone";

import { StyledDropzoneContainer } from "ui-library/Styled";
import { ErrorContent, Icon, Typography } from "ui-library/atoms";
import { Box, Flexbox } from "ui-library/layouts";

import i18n from "core/i18n";
import { FileItem } from "types";

import { useFileUploadField } from "./hooks/useFileUploadField";
import { useFormContext } from "react-hook-form";
import { ACCEPTED_FILE_SIZE_BYTES } from "core/form";

export interface FileDropAreaProps {
    name: string;
    disabled?: boolean;
    maxUploadedFiles?: number;
    multiple?: boolean;
    acceptedTypes?: Accept;
    maxFileSize?: number;
    onLoadFile?: (file: FileItem) => Promise<Partial<FileItem>>;
}

export const FileDropArea: React.FC<FileDropAreaProps> = ({
    name,
    disabled = false,
    maxUploadedFiles = 2,
    multiple = true,
    acceptedTypes = {
        "application/pdf": [".pdf", ".PDF"],
        "image/jpeg": [".jpg", ".jpeg", ".JPG", "JPEG"],
    },
    maxFileSize = ACCEPTED_FILE_SIZE_BYTES,
    onLoadFile,
}) => {
    const { t } = i18n.useTranslation();

    const {
        formState: { errors },
    } = useFormContext();
    const error = name.split(".").reduce((acc, part) => acc?.[part], errors);

    const { addFiles, files } = useFileUploadField(name, { onLoad: onLoadFile });
    const isDisabled = disabled || files.length >= maxUploadedFiles;

    const { getRootProps, getInputProps } = useDropzone({
        onDrop: addFiles,
        disabled: isDisabled,
        multiple,
        accept: acceptedTypes,
        maxFiles: maxUploadedFiles - files.length,
        maxSize: maxFileSize,
    });

    return (
        <>
            <StyledDropzoneContainer>
                <div {...getRootProps({ className: "dropzone" })} style={isDisabled ? { cursor: "not-allowed" } : {}}>
                    <input {...getInputProps()} />
                    <Flexbox alignItems={"center"} gap={3} direction={"column"}>
                        <Icon icon="document-upload" color={isDisabled ? "gray" : "blue"} size={32} />
                        <Box textAlign="center">
                            <Typography variant="span" markDown={t("form.uploadDocuments")} />
                            <Typography variant="span" markDown={`(${t("form.maximumNumberOfFiles")}: ${maxUploadedFiles})`} />
                            <br />
                            <Typography variant="caption" markDown={t("form.uploadDocumentsFormat")} />
                        </Box>
                    </Flexbox>
                </div>

                {Boolean(error) && <ErrorContent content={t(`error.attachment.${error?.message.toString()}`)} />}
            </StyledDropzoneContainer>
        </>
    );
};
